import React from 'react';
import styled from 'styled-components';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';

import { LoaderFixed } from '../../../../components/Loader';
import appConfig from '../../../../appConfig';
import GeoRegion from '../../../../components/GeoAddress/GeoRegion';
import { useGoogleMapsPlaces } from '../../../../components/GeoAddress/googleAPI';
import BlueshyftMapIcon from './blueshyft-map-icon.png';

const CashMap = () => {
	const [currentRegion, setCurrentRegion] = React.useState<Coordinates | undefined>(undefined);
	const [shops, setShops] = React.useState<CashShops[]>([]);
	const [activeMarkerId, setActiveMarkerId] = React.useState('');
	const [selectedRegionName, setSelectedRegionName] = React.useState<string>('');

	const isGoogleMapLoaded = useGoogleMapsPlaces();

	React.useEffect(() => {
		const getPositionSuccess = (position) => {
			setCurrentRegion({
				latitude: position.coords.latitude,
				longitude: position.coords.longitude,
			});
		};

		const getPositionError = (error) => {
			setCurrentRegion({
				latitude: -33.86559125374213,
				longitude: 151.20617624503282,
			});
		};

		navigator.geolocation.getCurrentPosition(getPositionSuccess, getPositionError);

		return () => {
			setCurrentRegion(undefined);
		};
	}, []);

	React.useEffect(() => {
		const getShopLocationsFromBlushyftApi = async () => {
			try {
				if (!currentRegion || !currentRegion?.latitude || !currentRegion?.longitude) {
					return;
				}

				const res = await fetch(
					`${appConfig.portal.blueshyftGeneralApi}locations?apikey=${appConfig.portal.blueshyftGeneralApiKey}`
				);

				const jsonRes = await res.json();

				if (jsonRes.errorKey) throw new Error(jsonRes.errorDetail);

				if (jsonRes?.locations) {
					const cashShops: CashShops[] = [];
					jsonRes.locations.forEach((location) => {
						if (
							location.shopId &&
							location.shopName &&
							location.displayAddress &&
							location.validatedAddress &&
							location.validatedAddress.latitude &&
							location.validatedAddress.longitude &&
							location.validatedAddress?.postcode < 2080
						) {
							cashShops.push({
								shopId: location.shopId,
								shopName: location.shopName,
								distance: getDistanceBetweenTwoCoordinates(
									currentRegion.latitude,
									currentRegion.longitude,
									location.validatedAddress?.latitude,
									location.validatedAddress?.longitude
								),
								displayAddress: location.displayAddress,
								postcode: location.validatedAddress?.postcode,
								coordinate: {
									latitude: location.validatedAddress?.latitude,
									longitude: location.validatedAddress?.longitude,
								},
							});
						}
					});
					setShops(cashShops);
				}
			} catch (e) {
				console.log('Blueshyft locations api error', e);
			}
		};

		getShopLocationsFromBlushyftApi();

		return () => {
			setShops([]);
		};
	}, [currentRegion]);

	const getDistanceBetweenTwoCoordinates = (lat1?: number, lon1?: number, lat2?: number, lon2?: number) => {
		if (
			!lat1 ||
			!lon1 ||
			!lat2 ||
			!lon2 ||
			Number.isNaN(lat1) ||
			Number.isNaN(lon1) ||
			Number.isNaN(lat2) ||
			Number.isNaN(lon2)
		) {
			return '';
		}

		const deg2rad = (deg) => {
			return deg * (Math.PI / 180);
		};

		const R = 6371; // Radius of the earth in km
		const dLat = deg2rad(lat2 - lat1);
		const dLon = deg2rad(lon2 - lon1);
		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		const d = R * c; // Distance in km

		return Number.isNaN(d) ? undefined : d.toFixed(1);
	};

	const userSelectsRegion = (displayName: string, latitude: number, longitude: number) => {
		setSelectedRegionName(displayName);
		setCurrentRegion({ latitude, longitude });
	};

	const findDirection = () => {
		const selectedShop = shops.find((s) => s.shopId === activeMarkerId);
		window.open(
			`https://www.google.com/maps/dir/?api=1&origin=${currentRegion?.latitude},${
				currentRegion?.longitude
			}&destination=${encodeURI(selectedShop?.displayAddress || '')}&travelmode=driving`
		);
	};

	const generateMarkers = () =>
		shops.map((shop) => (
			<Marker
				key={shop.shopId}
				position={{ lat: shop.coordinate.latitude, lng: shop.coordinate.longitude }}
				onClick={() => setActiveMarkerId(shop.shopId)}
				icon={BlueshyftMapIcon}
			>
				{activeMarkerId === shop.shopId && (
					<InfoWindow>
						<TextContainer>
							<BoldText>
								{shop.shopName}
								{shop.distance && <LightText>{` (${shop.distance}km)`}</LightText>}
							</BoldText>

							<NormalText>{shop.displayAddress}</NormalText>
							<DirectionText onClick={findDirection}>Direction</DirectionText>
						</TextContainer>
					</InfoWindow>
				)}
			</Marker>
		));

	if (!isGoogleMapLoaded || !currentRegion || !currentRegion.latitude || !currentRegion.longitude) {
		return <LoaderFixed />;
	}

	return (
		<CashMapContainer>
			<GeoRegion
				label={'Enter suburb or post code'}
				countryCode="AU"
				name="GeoRegionWeb"
				selectedRegion={selectedRegionName}
				onUserSelectsRegion={userSelectsRegion}
			></GeoRegion>
			<GoogleMap
				mapContainerStyle={{ height: '400px', marginTop: '10px' }}
				center={{
					lat: currentRegion.latitude,
					lng: currentRegion.longitude,
				}}
				zoom={14}
			>
				{generateMarkers()}
			</GoogleMap>
		</CashMapContainer>
	);
};

export default CashMap;

type Coordinates = {
	latitude: number;
	longitude: number;
};

type CashShops = {
	shopId: string;
	shopName: string;
	distance?: string;
	displayAddress: string;
	postcode: number;
	coordinate: { latitude: number; longitude: number };
};

const CashMapContainer = styled.div`
	margin-top: 12px;
	margin-bottom: 12px;
`;

const TextContainer = styled.div`
	width: 150px;
	user-select: none;

	& > p {
		margin-bottom: 5px;
	}
`;

const BoldText = styled.p`
	font-weight: bold;
	color: #3b4a3f;
`;

const NormalText = styled.p`
	color: #3b4a3f;
`;

const LightText = styled.span`
	font-weight: normal;
	color: #727272;
`;

const DirectionText = styled.p`
	cursor: pointer;
	color: #3182ce;
`;
